import { FieldValue } from "backend/datainput";
import { ICardElementWithChildren } from "backend/model/model";
import * as types from "client-api-workflow/types";
import ValueInputForm from "design/components/forms/ValueInputForm";
import { t } from "i18next";
import React, { useState } from "react";
import { getDateFromMEAValue } from "utils/utils";
import * as validators from "./validator";
import validateInputForm from "./validator/validateInputForm";
import ActionDialog from "./ActionDialog";
import useAllActions, { type ActionData } from "backend/hooks/actions/useAllActions";
import ActionsPreview from "design/components/cards/ActionsPreview";

const InitiativeInputForm = (props: {
	initiative: ICardElementWithChildren;
	childElements: ICardElementWithChildren[];
	comments?: types.WFComment[];
	attachments?: types.FileValue[];
	translatedName: string;
	onSubmit?: (values: FieldValue[]) => void;
	isReadOnly?: boolean;
}) => {
	const { initiative, childElements, attachments, comments } = props;

	const [milestoneValues, setMilestoneValues] = useState<FieldValue[]>([]);
	const [isActionDialogOpen, setIsActionDialogOpen] = useState(false);

	const { data: allActions } = useAllActions();
	const actions: ActionData[] | undefined = React.useMemo(
		() =>
			allActions?.filter(
				(action) => action.searchKpiInitiativeId === (initiative?.id || "")
			),
		[allActions, initiative?.id]
	);

	// Get Initiative info for the action dialog
	const initiativeInfo = {
		id: initiative.id || "",
		typename: "Initiative" as const,
		name: initiative.name || "",
	};

	const milestones = React.useMemo(() => {
		return (childElements || []).filter(
			(item) => item.typename === "Milestone"
		);
	}, [childElements]);

	const getDateValue = (value?: Date) => {
		if (!value) return "";
		return `${value.getFullYear()}-${(value.getMonth() + 1)
			.toString()
			.padStart(2, "0")}-${value.getDate().toString().padStart(2, "0")}`;
	};

	return (
		<>
			<ValueInputForm
				key={initiative?.workflowInstance?.wfInstanceId}
				element={initiative!}
				childElements={childElements}
				buttons={[
					{
						tag: "submit",
						style: "submit",
						text: t("workflowinput.form.Submit").toString(),
						onClick: (values) => {
							console.log("initiative values", values);
							if (milestones.length > 0 && milestoneValues.length === 0) {
								return;
							}

							if (props.onSubmit) {
								const allValues = [...values, ...milestoneValues].map((v) => {
									if (v.field.element) {
										v.field.element.workflowInstance =
											initiative.workflowInstance;
									}
									return v;
								});
								console.log("allValues", allValues);
								props.onSubmit(allValues);
							}
						},
					},
				]}
				onIntermediateChange={(values) => {}}
				fields={[
					{
						title: t("workflowinput.form.ACTUAL_END_DATE").toString(),
						series: "ACTUAL_END_DATE",
						element: initiative,
						inputType: "datetime",
						initialValue: initiative.ACTUAL_END_DATE
							? getDateValue(getDateFromMEAValue(initiative.ACTUAL_END_DATE))
							: undefined,
						isDisabled: props.isReadOnly,
						validate: (state) => {
							return validateInputForm(state, [validators.isNotEmpty]);
						},
					},
					{
						title: t("workflowinput.form.COMPLETION_PERCENTAGE").toString(),
						series: "COMPLETION_PERCENTAGE",
						element: initiative,
						inputType: "string",
						unitSeries: "COMPLETION_PERCENTAGE_unit",
						initialValue: initiative.COMPLETION_PERCENTAGE,
						isDisabled: props.isReadOnly,
						validate: (state) => {
							return validateInputForm(state, [
								validators.isNotEmpty,
								validators.isNumber,
							]);
						},
					},
					{
						title: t("workflowinput.form.EXPENDITURE").toString(),
						series: "EXPENDITURE",
						element: initiative,
						inputType: "string",
						unitSeries: "EXPENDITURE_unit",
						isDisabled: props.isReadOnly,
						validate: (state) => {
							return validateInputForm(state, [
								validators.isNotEmpty,
								validators.isNumber,
							]);
						},
					},
					{
						title: t("workflowinput.form.Attachments"),
						element: initiative,
						inputType: "attachments",
						initialFiles: attachments,
						isDisabled: props.isReadOnly,
					},
					{
						// title: t("workflowinput.form.Actions and Recommendations"),
						title: t("Justifications & Recommendations"),
						element: initiative,
						inputType: "comment",
						comments: comments,
						isDisabled: props.isReadOnly,
						initialValue:
							comments && comments[0] ? comments[0].description : "",
					},
				]}
			/>

			{/* Add Action Button - appears after Actions and Recommendations */}
			<span style={{ fontSize: "18px", fontWeight: "bold" }}>Actions</span>
			{!props.isReadOnly && (
				<div style={{ marginTop: "16px", marginBottom: "16px" }}>
					<button
						type="button"
						onClick={() => setIsActionDialogOpen(true)}
						style={{
							backgroundColor: "#1976d2",
							color: "#fff",
							border: "none",
							borderRadius: "4px",
							padding: "8px 16px",
							cursor: "pointer",
							fontSize: "14px",
							fontWeight: 500,
							display: "flex",
							alignItems: "center",
							gap: "8px",
						}}
					>
						<span style={{ fontSize: "18px", fontWeight: "bold" }}>+</span>
						{t("Add Action")}
					</button>
				</div>
			)}

			{/* Action Dialog */}
			<ActionDialog
				open={isActionDialogOpen}
				isDisabled={!(
					initiative?.workflowInstance?.allowedActions?.includes("editcomments") === true
				)}
				onClose={() => setIsActionDialogOpen(false)}
				kpiOrInitiative={initiativeInfo}
			/>
			{/* Actions Preview */}
			<div style={{ marginBottom: "20px" }}>
				<ActionsPreview
					actions={actions || []}
					kpiInitiativeName={initiativeInfo.name}
				/>
			</div>
		</>
	);
};

export default InitiativeInputForm;
